import { type ComponentType, Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay'
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal'
import { DOCUMENT, isPlatformBrowser } from '@angular/common'
import {
    inject,
    Injectable,
    InjectionToken,
    Injector,
    PLATFORM_ID,
    TemplateRef,
    type ViewContainerRef,
} from '@angular/core'

import { ZardDialogRef } from './dialog-ref'
import { ZardDialogComponent, ZardDialogOptions } from './dialog.component'

type ContentType<T> = ComponentType<T> | TemplateRef<T> | string

export const Z_MODAL_DATA = new InjectionToken<unknown>('Z_MODAL_DATA')

const BODY_SCROLL_LOCK_CLASS = 'ei-dialog-open'
const APP_MAIN_SCROLL_SELECTOR = '.app__contentarea'

interface ILockedScrollElement {
    element: HTMLElement
    overflow: string
    overflowY: string
    overscrollBehavior: string
    pointerEvents: string
    scrollTop: number
}

@Injectable({
    providedIn: 'root',
})
export class ZardDialogService {
    private overlay = inject(Overlay)
    private injector = inject(Injector)
    private platformId = inject(PLATFORM_ID)
    private document = inject(DOCUMENT)
    private openDialogCount = 0
    private savedMainContentScrollTop = 0
    private lockedNestedScrollElements: ILockedScrollElement[] = []
    private backgroundScrollBlockersAttached = false

    create<T, U>(config: ZardDialogOptions<T, U>): ZardDialogRef<T> {
        return this.open<T, U>(config.zContent as ComponentType<T>, config)
    }

    private open<T, U>(componentOrTemplateRef: ContentType<T>, config: ZardDialogOptions<T, U>) {
        const overlayRef = this.createOverlay(config)

        if (!overlayRef) {
            return new ZardDialogRef(
                undefined as unknown as OverlayRef,
                config,
                undefined as unknown as ZardDialogComponent<T, U>,
                this.platformId,
            )
        }

        const dialogContainer = this.attachDialogContainer<T, U>(overlayRef, config)
        const dialogRef = this.attachDialogContent<T, U>(
            componentOrTemplateRef,
            dialogContainer,
            overlayRef,
            config,
        )

        dialogContainer.dialogRef = dialogRef

        return dialogRef
    }

    private getAppMainScrollEl(): HTMLElement | null {
        return this.document.querySelector(APP_MAIN_SCROLL_SELECTOR)
    }

    private collectScrollableElements(root: HTMLElement): HTMLElement[] {
        const scrollables: HTMLElement[] = []
        const elements = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))]

        for (const element of elements) {
            const style = this.document.defaultView?.getComputedStyle(element)
            if (!style) continue

            const hasVerticalScroll =
                style.overflowY === 'auto' ||
                style.overflowY === 'scroll' ||
                style.overflow === 'auto' ||
                style.overflow === 'scroll'

            if (hasVerticalScroll) {
                scrollables.push(element)
            }
        }

        return scrollables
    }

    private lockNestedScrollContainers(main: HTMLElement): void {
        const scrollables = this.collectScrollableElements(main)

        for (const element of scrollables) {
            if (element === main) continue

            this.lockedNestedScrollElements.push({
                element,
                overflow: element.style.overflow,
                overflowY: element.style.overflowY,
                overscrollBehavior: element.style.overscrollBehavior,
                pointerEvents: element.style.pointerEvents,
                scrollTop: element.scrollTop,
            })
            element.style.setProperty('overflow', 'hidden', 'important')
            element.style.setProperty('overscroll-behavior', 'none', 'important')
            element.style.setProperty('pointer-events', 'none', 'important')
        }
    }

    private releaseNestedScrollContainers(): void {
        for (const locked of this.lockedNestedScrollElements) {
            const { element, overflow, overflowY, overscrollBehavior, pointerEvents, scrollTop } =
                locked

            if (overflow) {
                element.style.overflow = overflow
            } else {
                element.style.removeProperty('overflow')
            }

            if (overflowY) {
                element.style.overflowY = overflowY
            } else {
                element.style.removeProperty('overflow-y')
            }

            if (overscrollBehavior) {
                element.style.overscrollBehavior = overscrollBehavior
            } else {
                element.style.removeProperty('overscroll-behavior')
            }

            if (pointerEvents) {
                element.style.pointerEvents = pointerEvents
            } else {
                element.style.removeProperty('pointer-events')
            }

            element.scrollTop = scrollTop
        }

        this.lockedNestedScrollElements = []
    }

    private isEventInsideOverlayPane(target: Node): boolean {
        const overlayPanes = this.document.querySelectorAll('.cdk-overlay-pane')
        return Array.from(overlayPanes).some((pane) => pane.contains(target))
    }

    private readonly blockBackgroundScroll = (event: Event): void => {
        const target = event.target
        if (!(target instanceof Node)) return
        if (this.isEventInsideOverlayPane(target)) return
        event.preventDefault()
    }

    private attachBackgroundScrollBlockers(): void {
        if (this.backgroundScrollBlockersAttached) return

        this.document.addEventListener('wheel', this.blockBackgroundScroll, {
            passive: false,
            capture: true,
        })
        this.document.addEventListener('touchmove', this.blockBackgroundScroll, {
            passive: false,
            capture: true,
        })
        this.backgroundScrollBlockersAttached = true
    }

    private detachBackgroundScrollBlockers(): void {
        if (!this.backgroundScrollBlockersAttached) return

        this.document.removeEventListener('wheel', this.blockBackgroundScroll, { capture: true })
        this.document.removeEventListener('touchmove', this.blockBackgroundScroll, {
            capture: true,
        })
        this.backgroundScrollBlockersAttached = false
    }

    private addBodyScrollLock(): void {
        if (!isPlatformBrowser(this.platformId)) return
        this.openDialogCount++
        if (this.openDialogCount === 1) {
            const html = this.document.documentElement
            const body = this.document.body
            const main = this.getAppMainScrollEl()

            this.savedMainContentScrollTop = main?.scrollTop ?? 0

            html.classList.add(BODY_SCROLL_LOCK_CLASS)
            body.classList.add(BODY_SCROLL_LOCK_CLASS)
            html.style.setProperty('overflow', 'hidden', 'important')
            body.style.setProperty('overflow', 'hidden', 'important')
            if (main) {
                main.style.setProperty('overflow', 'hidden', 'important')
                main.style.setProperty('overscroll-behavior', 'none', 'important')
                main.style.setProperty('pointer-events', 'none', 'important')
                this.lockNestedScrollContainers(main)
            }

            this.attachBackgroundScrollBlockers()
        }
    }

    private removeBodyScrollLock(): () => void {
        return () => {
            if (!isPlatformBrowser(this.platformId)) return
            this.openDialogCount = Math.max(0, this.openDialogCount - 1)
            if (this.openDialogCount === 0) {
                const html = this.document.documentElement
                const body = this.document.body
                const main = this.getAppMainScrollEl()

                html.classList.remove(BODY_SCROLL_LOCK_CLASS)
                body.classList.remove(BODY_SCROLL_LOCK_CLASS)
                html.style.removeProperty('overflow')
                body.style.removeProperty('overflow')
                if (main) {
                    main.style.removeProperty('overflow')
                    main.style.removeProperty('overscroll-behavior')
                    main.style.removeProperty('pointer-events')
                    main.scrollTop = this.savedMainContentScrollTop
                }

                this.releaseNestedScrollContainers()
                this.detachBackgroundScrollBlockers()
            }
        }
    }

    private createOverlay<T, U>(config: ZardDialogOptions<T, U>): OverlayRef | undefined {
        if (isPlatformBrowser(this.platformId)) {
            this.addBodyScrollLock()
            const positionStrategy = this.overlay.position().global()
            if (config.zPosition?.left) {
                positionStrategy.left(config.zPosition.left)
            } else if (config.zPosition?.right) {
                positionStrategy.right(config.zPosition.right)
            } else {
                positionStrategy.centerHorizontally()
            }
            if (config.zPosition?.top) {
                positionStrategy.top(config.zPosition.top)
            } else if (config.zPosition?.bottom) {
                positionStrategy.bottom(config.zPosition.bottom)
            } else {
                positionStrategy.centerVertically()
            }

            const backdropClass = config.zBackdropClass
                ? Array.isArray(config.zBackdropClass)
                    ? config.zBackdropClass
                    : [config.zBackdropClass]
                : ['cdk-overlay-dark-backdrop']

            const overlayConfig = new OverlayConfig({
                hasBackdrop: true,
                backdropClass,
                positionStrategy,
                scrollStrategy: this.overlay.scrollStrategies.block(),
            })

            return this.overlay.create(overlayConfig)
        }

        return undefined
    }

    private attachDialogContainer<T, U>(overlayRef: OverlayRef, config: ZardDialogOptions<T, U>) {
        const injector = Injector.create({
            parent: this.injector,
            providers: [
                { provide: OverlayRef, useValue: overlayRef },
                { provide: ZardDialogOptions, useValue: config },
            ],
        })

        const containerPortal = new ComponentPortal<ZardDialogComponent<T, U>>(
            ZardDialogComponent,
            config.zViewContainerRef,
            injector,
        )

        const containerRef = overlayRef.attach<ZardDialogComponent<T, U>>(containerPortal)

        return containerRef.instance
    }

    private attachDialogContent<T, U>(
        componentOrTemplateRef: ContentType<T>,
        dialogContainer: ZardDialogComponent<T, U>,
        overlayRef: OverlayRef,
        config: ZardDialogOptions<T, U>,
    ) {
        const onDispose = this.removeBodyScrollLock()
        const dialogRef = new ZardDialogRef<T>(
            overlayRef,
            config,
            dialogContainer,
            this.platformId,
            onDispose,
        )

        if (componentOrTemplateRef instanceof TemplateRef) {
            dialogContainer.attachTemplatePortal(
                new TemplatePortal<T>(
                    componentOrTemplateRef,
                    null as unknown as ViewContainerRef,
                    {
                        dialogRef,
                    } as T,
                ),
            )
        } else if (typeof componentOrTemplateRef !== 'string') {
            const injector = this.createInjector<T, U>(dialogRef, config)
            const contentRef = dialogContainer.attachComponentPortal<T>(
                new ComponentPortal(componentOrTemplateRef, config.zViewContainerRef, injector),
            )
            dialogRef.componentInstance = contentRef.instance
        }

        return dialogRef
    }

    private createInjector<T, U>(dialogRef: ZardDialogRef<T>, config: ZardDialogOptions<T, U>) {
        return Injector.create({
            parent: this.injector,
            providers: [
                { provide: ZardDialogRef, useValue: dialogRef },
                { provide: Z_MODAL_DATA, useValue: config.zData },
            ],
        })
    }
}
